<template>
  <div class="flex flex-col">
    <div v-if="privateRequest" class="success-hero">
      <Rocket class="stroke-[1.5px]" />
      <p class="success-hero__title">
        {{ $t('dateRequest.successHero.title') }}
      </p>
    </div>
    <div v-else class="complete-alert">
      <Rocket class="w-[19px] h-[19px]" />
      <p>{{ $t('dateRequest.eventSuggestions.requestSent') }}</p>
    </div>

    <a
      v-if="privateRequest"
      :href="corporateGiftingHref"
      target="_blank"
      rel="noopener"
      class="corporate-gifting-banner"
    >
      <IconGift class="w-[156px] h-[112px]" />
      <p class="corporate-gifting-banner__title">
        {{ $t('dateRequest.corporateGifting.title') }}
      </p>
      <p class="corporate-gifting-banner__description">
        {{ $t('dateRequest.corporateGifting.description') }}
      </p>
      <span class="corporate-gifting-banner__cta">
        {{ $t('dateRequest.corporateGifting.cta') }}
      </span>
    </a>

    <DateRequestProductList
      :private-request="privateRequest"
      data-test-id="date-request-similar-events"
      is-single-column
      :requested="true"
      :loading="similarEventsLoading"
      :products="eventsRequested"
    />

    <template v-if="eventsWithBookableDate.length > 0">
      <div class="font-medium my-3">
        {{ $t('dateRequest.eventSuggestions.bookableExperiencesTitle') }}
      </div>

      <DateRequestProductList
        :private-request="privateRequest"
        data-test-id="date-request-bookable-events"
        is-single-column
        :loading="similarEventsLoading"
        :products="eventsWithBookableDate"
        @request-success="handleRequestSuccess"
      />
    </template>

    <template v-if="eventsWithoutBookableDate.length > 0">
      <div class="my-3">
        <div class="font-medium">
          {{
            privateRequest
              ? $t('dateRequest.eventSuggestions.title')
              : $t('dateRequest.eventSuggestions.dateRequestTitle')
          }}
        </div>
        <div v-if="privateRequest" class="text-sm text-stone-600">
          {{ $t('dateRequest.eventSuggestions.subtitle') }}
        </div>
      </div>

      <DateRequestProductList
        :private-request="privateRequest"
        data-test-id="date-request-similar-events"
        is-single-column
        :loading="similarEventsLoading"
        :products="eventsWithoutBookableDate"
        @request-success="handleRequestSuccess"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { Rocket } from 'lucide-vue-next'
import DateRequestProductList from './DateRequestProductList.vue'
import IconGift from '~/assets/general/gift.svg?component'
import type { DateRequestSimilarEvent } from '~/composables/types/event'
import { useFetchDateRequestSimilarEvents } from '~/composables/product'

const { t: $t, locale } = useI18n()
const localePath = useLocalePath()

const { dateRequestId = undefined, privateRequest = false } = defineProps<{
  dateRequestId?: string
  /** Set for the private-event-request flow; shows the corporate gifting
   *  hero instead of the plain date-request confirmation banner. */
  privateRequest?: boolean
}>()

const corporateGiftingHref = computed(() =>
  localePath(
    locale.value === 'de-de'
      ? '/b2b/mitarbeitergeschenke'
      : '/b2b/employee-gifts'
  )
)

const requestedEventIds = ref<string[]>([])
const scrollDateRequestDrawerToTop = inject<() => void>(
  'scrollDateRequestDrawerToTop',
  () => {}
)
defineEmits<{
  onCloseDateRequest: []
}>()

const { data: similarEventsResponse, pending: similarEventsLoading } =
  useFetchDateRequestSimilarEvents(dateRequestId || '', {
    lazy: true,
    server: false,
    default: () =>
      ({
        data: { id: '', private: false, similar_events: { data: [] } },
      }) as any,
  })

const similarEvents = computed(
  () => similarEventsResponse.value?.data?.similar_events?.data || []
)

const handleRequestSuccess = (eventData: DateRequestSimilarEvent) => {
  const eventId = eventData.id
  requestedEventIds.value.push(eventId)

  nextTick(() => scrollDateRequestDrawerToTop())
}

const eventsNotRequested = computed(
  () =>
    similarEvents.value
      ?.filter(
        (event: DateRequestSimilarEvent) =>
          !requestedEventIds.value.includes(event.id)
      )
      .slice(0, 6) ?? []
)

const eventsWithBookableDate = computed(
  () =>
    eventsNotRequested.value?.filter(
      (event: DateRequestSimilarEvent) => event.bookable_date
    ) ?? []
)

const eventsWithoutBookableDate = computed(
  () =>
    eventsNotRequested.value?.filter(
      (event: DateRequestSimilarEvent) => !event.bookable_date
    ) ?? []
)

const eventsRequested = computed(
  () =>
    similarEvents.value?.filter((event: DateRequestSimilarEvent) =>
      requestedEventIds.value.includes(event.id)
    ) ?? []
)
</script>

<style lang="postcss" scoped>
.complete-alert {
  @apply p-4  flex gap-2 items-center justify-center bg-success-light rounded-lg text-sm mt-1;
}

.success-hero {
  @apply flex items-center justify-center gap-2 py-6 pt-9 text-center;
}

.success-hero__title {
  @apply text-lg font-semibold text-stone-800;
}

.corporate-gifting-banner {
  @apply flex flex-col items-center gap-2 rounded-2xl border border-lime-400 bg-lime-50 p-6 text-center mb-4 md:mx-16;
  @apply transition-colors hover:bg-lime-100;
  @apply hover:scale-[1.02] transition-all;
}

.corporate-gifting-banner__title {
  @apply text-base font-semibold text-stone-800;
}

.corporate-gifting-banner__description {
  @apply text-sm text-stone-600;
}

.corporate-gifting-banner__cta {
  @apply w-full mt-2 mx-4 rounded-lg border border-teal-900 px-4 py-2 text-sm font-semibold text-teal-900;
  @apply hover:bg-teal-800 hover:text-white;
}

/* Style ProductCard title to be smaller inside DateRequestSuccess */
:deep(.product-card__title) {
  @apply text-xs md:text-sm;
}
</style>
